import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Upload } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Motion";
import { api, formatApiError } from "@/lib/api";

const inputCls = "nx-input";

const JobDetail = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", linkedin: "", cover_note: "", website: "", consent: false });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState("");

  useEffect(() => {
    setJob(null);
    setNotFound(false);
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setNotFound(true));
  }, [id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("job_id", job.id);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("source_page", "job-detail");
      if (resume) fd.append("resume", resume);
      const r = await api.post("/applications", fd);
      setReference(r.data.reference);
      toast.success("Application submitted successfully.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <section className="flex min-h-screen items-center bg-obsidian" data-testid="job-not-found">
        <div className="nx-container py-40">
          <p className="font-heading text-3xl font-light text-white">This mandate is no longer available.</p>
          <Link to="/jobs" className="nx-btn-outline mt-8" data-testid="job-not-found-back-btn">
            <ArrowLeft size={15} /> Back to Opportunities
          </Link>
        </div>
      </section>
    );
  }

  if (!job) {
    return (
      <section className="min-h-screen bg-obsidian">
        <div className="nx-container py-48">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/35">Loading mandate…</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <Seo
        title={`${job.title} — ${job.location} — NEXREQ UAE & GCC`}
        siteName="NEXREQ"
        description={job.summary}
      />
      <section className="border-b border-white/[0.08] bg-obsidian pb-16 pt-40 lg:pt-48" data-testid="job-header">
        <div className="nx-container">
          <Link
            to="/jobs"
            className="inline-flex items-center gap-2 text-sm text-white/50 transition-colors hover:text-emerald-400"
            data-testid="job-back-link"
          >
            <ArrowLeft size={14} /> All Opportunities
          </Link>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
            <span>{job.reference}</span>
            <span className="text-emerald-500">{job.type}</span>
            {job.demo && <span className="border border-white/20 px-2 py-0.5 text-white/50">Demonstration Listing</span>}
            <span>{job.sector}</span>
            <span>{job.seniority}</span>
          </div>
          <h1 className="mt-6 max-w-4xl font-heading text-4xl font-light leading-[1.06] tracking-tight text-white sm:text-5xl">
            {job.title}
          </h1>
          <p className="mt-4 text-lg text-white/50">
            {job.location} {job.salary ? `· ${job.salary}` : ""}
          </p>
        </div>
      </section>

      <section className="bg-obsidian py-16 lg:py-24">
        <div className="nx-container grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-lg leading-relaxed text-white/65">{job.description}</p>
            </Reveal>
            {job.responsibilities?.length > 0 && (
              <Reveal delay={0.1}>
                <div className="mt-12" data-testid="job-responsibilities">
                  <Eyebrow>The Mandate</Eyebrow>
                  <ul className="mt-6 space-y-3">
                    {job.responsibilities.map((r) => (
                      <li key={r} className="flex items-start gap-3 text-[15px] text-white/65">
                        <Check size={15} className="mt-1 shrink-0 text-emerald-500" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}
            {job.requirements?.length > 0 && (
              <Reveal delay={0.15}>
                <div className="mt-12" data-testid="job-requirements">
                  <Eyebrow>The Profile</Eyebrow>
                  <ul className="mt-6 space-y-3">
                    {job.requirements.map((r) => (
                      <li key={r} className="flex items-start gap-3 text-[15px] text-white/65">
                        <Check size={15} className="mt-1 shrink-0 text-emerald-500" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <div className="border border-white/10 bg-surface p-8" data-testid="job-apply-panel">
                {reference ? (
                  <div data-testid="application-success">
                    <span className="flex h-11 w-11 items-center justify-center bg-emerald-600/15 text-emerald-400">
                      <Check size={20} />
                    </span>
                    <h2 className="mt-6 font-heading text-2xl font-light text-white">Application Received.</h2>
                    <p className="mt-4 text-sm leading-relaxed text-white/55">
                      Thank you — your application for <strong className="text-white/80">{job.title}</strong> has
                      been received. Our consultants will review your profile and contact you if there
                      is a strong match.
                    </p>
                    <p className="mt-6 border border-white/10 bg-obsidian px-4 py-3 font-mono text-xs tracking-[0.15em] text-emerald-400">
                      REF: {reference}
                    </p>
                    <Link to="/jobs" className="nx-btn-outline mt-8 w-full" data-testid="application-success-back-btn">
                      Browse More Roles
                    </Link>
                  </div>
                ) : (
                  <form onSubmit={submit}>
                    <h2 className="font-heading text-2xl font-light text-white">Apply for This Role</h2>
                    <p className="mt-2 text-sm text-white/45">
                      Confidential. Reviewed by a consultant, never a bot.
                    </p>
                    <div className="mt-7 space-y-5">
                      <div>
                        <label className="nx-label" htmlFor="ap-name">Full Name *</label>
                        <input id="ap-name" className={inputCls} required value={form.name} onChange={set("name")} data-testid="apply-name-input" />
                      </div>
                      <div>
                        <label className="nx-label" htmlFor="ap-email">Email *</label>
                        <input id="ap-email" type="email" className={inputCls} required value={form.email} onChange={set("email")} data-testid="apply-email-input" />
                      </div>
                      <div className="grid gap-5 sm:grid-cols-2">
                        <div>
                          <label className="nx-label" htmlFor="ap-phone">Phone</label>
                          <input id="ap-phone" className={inputCls} value={form.phone} onChange={set("phone")} data-testid="apply-phone-input" />
                        </div>
                        <div>
                          <label className="nx-label" htmlFor="ap-location">Current Location</label>
                          <input id="ap-location" className={inputCls} value={form.location} onChange={set("location")} data-testid="apply-location-input" />
                        </div>
                      </div>
                      <div>
                        <label className="nx-label" htmlFor="ap-linkedin">LinkedIn Profile</label>
                        <input id="ap-linkedin" className={inputCls} placeholder="https://linkedin.com/in/…" value={form.linkedin} onChange={set("linkedin")} data-testid="apply-linkedin-input" />
                      </div>
                      <div>
                        <label className="nx-label" htmlFor="ap-note">Cover Note</label>
                        <textarea id="ap-note" rows={4} className={inputCls} value={form.cover_note} onChange={set("cover_note")} data-testid="apply-note-input" />
                      </div>
                      <div>
                        <label className="nx-label">Resume (PDF / Word, max 8MB)</label>
                        <label
                          className="flex cursor-pointer items-center gap-3 border border-dashed border-white/15 bg-white/[0.02] px-4 py-4 text-sm text-white/50 transition-colors hover:border-emerald-500/50 hover:text-white/70"
                          data-testid="apply-resume-dropzone"
                        >
                          <Upload size={16} className="shrink-0 text-emerald-500" />
                          {resume ? resume.name : "Attach your resume"}
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            className="hidden"
                            onChange={(e) => setResume(e.target.files?.[0] || null)}
                            data-testid="apply-resume-input"
                          />
                        </label>
                      </div>
                      <input
                        type="text"
                        name="website"
                        className="hidden"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={set("website")}
                      />
                      <label className="flex items-start gap-3 text-xs leading-relaxed text-white/50" data-testid="apply-consent-label">
                        <input type="checkbox" required checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-600" data-testid="apply-consent-checkbox" />
                        <span>I consent to NEXREQ processing my personal data for recruitment purposes, as described in the <Link to="/privacy" className="text-emerald-400 hover:underline">Privacy Policy</Link>.</span>
                      </label>
                      <button type="submit" className="nx-btn-primary w-full" disabled={submitting} data-testid="apply-submit-btn">
                        {submitting ? "Submitting…" : "Submit Application"} <ArrowRight size={15} />
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default JobDetail;
